import { useCallback, useContext } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { replaceWithRegion } from 'actions/NavigationActions'
import * as Analytics from 'analytics/analytics'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { useGenerateMultiDestinationTrip, useUpdateTrip } from 'tripPlanner/hooks/api/trip'
import { getTripURL } from 'tripPlanner/utils'
import { GenerateMultiDestinationTripRequest } from 'tripPlanner/api/types'
import moment from 'moment'
import {
  TripPaceOption,
  ActivityBudgetOption,
  FoodStyleOption,
  MustDoOption,
  DietaryRequirementOption,
  ProximityOption,
  OccupancyOption,
  DatePickerOption,
  Locations,
} from '../GenerateTripPage'
import ContactContext from 'contexts/contactContext'
import { FullTrip } from 'tripPlanner/types/common'
import offerPageURL from 'lib/offer/offerPageURL'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { isLEStaff } from 'selectors/accountSelectors'

interface UseTripGenerationProps {
  orderId: string | undefined
  cartId: string | undefined
  offerIdFromUrl: string | undefined
  urlLocation: string | undefined
  hotelPlace: App.Place | undefined
  offer: App.AnyOffer | App.OrderItemOffer | undefined
  setShouldGenerateActivities: (value: boolean | null) => void
  setAnalyticsType: (type: 'customised' | 'non-customised') => void
  setAnalyticsUserChoices: (choices: Array<string>) => void
  setNewTripId: (id: string | undefined) => void
}

function getEventSubject(type: 'customised' | 'non-customised', hotelCity: string, orderId: string | undefined, cartId: string | undefined, offerIdFromUrl: string | undefined, tripId: string, userChoices: Array<string>, locations: Array<Locations>) {
  let source = ''
  if (orderId) {
    source = 'From Order'
  } else if (cartId) {
    source = 'From Cart'
  } else if (offerIdFromUrl) {
    source = 'From Offer'
  } else {
    source = 'From Location'
  }

  // Use location names when hotelCity is empty (multi-destination flow)
  const cityName = hotelCity || (locations.length > 0 ? locations.map(loc => loc.locationName).join(', ') : '')

  if (type === 'non-customised') {
    return `${cityName} - ${source} || ${tripId}`
  } else {
    return `${cityName} - ${source} || ${userChoices.join(' || ')} || ${tripId}`
  }
}

export function useTripGeneration({
  orderId,
  cartId,
  offerIdFromUrl,
  urlLocation,
  hotelPlace,
  offer,
  setShouldGenerateActivities,
  setAnalyticsType,
  setAnalyticsUserChoices,
  setNewTripId,
}: UseTripGenerationProps) {
  const isStaff = useAppSelector(isLEStaff)

  const variation = isStaff ? '2' : undefined

  const dispatch = useAppDispatch()
  const currentContact = useContext(ContactContext)

  const updateTripMutation = useUpdateTrip({
    onError: (error) => {
      console.warn('Failed to auto-update trip destination:', error)
    },
  })

  const generateMultiDestinationTripMutation = useGenerateMultiDestinationTrip({
    onSuccess: (result) => {
      if (result?.result?.id) {
        setNewTripId(result.result.id)

        if (hotelPlace?.id) {
          updateTripMutation.mutate({
            tripId: result.result.id,
            destinationPlaceIds: [hotelPlace.id],
          })
        }
      }
    },
    onError: (error) => {
      console.warn('Failed to generate multi-destination trip:', error)
      showSnackbar(
        'Something went wrong, please try again or contact us.',
        'critical',
        {
          action: {
            label: `Call ${currentContact.defaultContact.humanReadable}`,
            href: `tel:${currentContact.defaultContact.number}`,
          },
        },
      )
      if (orderId) {
        dispatch(replaceWithRegion(`/account/my-escapes/order/${orderId}`))
      } else if (cartId) {
        dispatch(replaceWithRegion(`/checkout-restore-cart?cartId=${cartId}`))
      } else if (offerIdFromUrl) {
        dispatch(replaceWithRegion(offerPageURL(offer)))
      } else if (urlLocation) {
        dispatch(replaceWithRegion('/trip-planner'))
      } else {
        dispatch(replaceWithRegion('/'))
      }
    },
  })

  const handleGenerateTrip = useCallback(async(
    type: 'customised' | 'non-customised',
    themes: Array<{
      theme: string
      selected: boolean
    }>,
    generateActivities: boolean,
    tripPace: Array<TripPaceOption>,
    activityBudget: Array<ActivityBudgetOption>,
    foodStyle: Array<FoodStyleOption>,
    mustDoActivities: Array<MustDoOption>,
    dietaryRequirements: Array<DietaryRequirementOption>,
    proximity: Array<ProximityOption>,
    freeText: string,
    location: string,
    occupancy: OccupancyOption,
    datePicker: DatePickerOption,
    locations: Array<Locations>,
  ) => {
    if (!orderId && !cartId && !offerIdFromUrl && !location && (!locations || locations.length === 0)) {
      return
    }

    setShouldGenerateActivities(generateActivities)

    const transformedLocations = locations.map(loc => ({
      locationName: loc.locationName,
      startDate: loc.startDate,
      endDate: loc.endDate,
      occupancy: loc.occupancy,
      duration: loc.duration,
    }))

    const tripStartDate = locations.length > 0 && locations[0].startDate
      ? locations[0].startDate
      : datePicker.startDate ? moment(datePicker.startDate).format(ISO_DATE_FORMAT) : undefined

    const tripEndDate = locations.length > 0 && locations[locations.length - 1].endDate
      ? locations[locations.length - 1].endDate
      : datePicker.endDate ? moment(datePicker.endDate).format(ISO_DATE_FORMAT) : undefined

    const params: GenerateMultiDestinationTripRequest = {
      orderId: orderId,
      cartId: cartId,
      offerId: offer?.id,
      generateActivities: generateActivities,
      tripPace: tripPace,
      activityBudget: activityBudget,
      foodStyle: foodStyle,
      mustDoActivities: mustDoActivities,
      themes: themes,
      dietaryRequirements: dietaryRequirements,
      proximity: proximity,
      freeText: freeText,
      occupancy: occupancy,
      variation: variation,
      locations: transformedLocations,
      startDate: tripStartDate,
      endDate: tripEndDate,
    }

    const userChoices = [
      ...themes.filter(item => item.selected).map(item => item.theme),
      ...tripPace.filter(item => item.selected).map(item => item.tripPace),
      ...activityBudget.filter(item => item.selected).map(item => item.activityBudget),
      ...foodStyle.filter(item => item.selected).map(item => item.foodStyle),
      ...mustDoActivities.filter(item => item.selected).map(item => item.activity),
      ...dietaryRequirements.filter(item => item.selected).map(item => item.dietaryRequirement),
      ...proximity.filter(item => item.selected).map(item => item.proximity),
      freeText,
      location,
      ...locations.map(loc => loc.locationName),
      occupancy.adults.toString(),
      occupancy.children?.toString() || '',
      datePicker.startDate?.toISOString() || '',
      datePicker.endDate?.toISOString() || '',
    ]

    setAnalyticsType(type)
    setAnalyticsUserChoices(userChoices)

    generateMultiDestinationTripMutation.mutate(params)
  }, [orderId, cartId, offerIdFromUrl, offer, setShouldGenerateActivities, setAnalyticsType, setAnalyticsUserChoices, generateMultiDestinationTripMutation, variation])

  const handleTripSuccess = useCallback((
    trip: FullTrip,
    shouldGenerateActivities: boolean | null,
    analyticsType: 'customised' | 'non-customised',
    analyticsUserChoices: Array<string>,
    hotelPlace: App.Place | undefined,
    hotelCity: string,
    locations: Array<Locations>,
  ) => {
    if (trip.generationStatus === 'IN_PROGRESS' || trip.generationStatus === 'SUCCESS' || !shouldGenerateActivities) {
      const subject = getEventSubject(analyticsType, hotelPlace?.name || hotelCity, orderId, cartId, offer?.id, trip.id, analyticsUserChoices, locations)

      Analytics.trackClientEvent({
        subject,
        action: 'click',
        category: 'ai-generated-trip',
        type: 'interaction',
      })

      dispatch(replaceWithRegion(getTripURL(trip.id)))
    } else if (trip.generationStatus === 'FAILURE') {
      showSnackbar(
        'Something went wrong, please try again or contact us.',
        'success',
        {
          action: {
            label: `Call ${currentContact.defaultContact.humanReadable}`,
            href: `tel:${currentContact.defaultContact.number}`,
          },
        },
      )
      if (orderId) {
        dispatch(replaceWithRegion(`/account/my-escapes/order/${orderId}`))
      } else if (cartId) {
        dispatch(replaceWithRegion(`/checkout-restore-cart?cartId=${cartId}`))
      } else if (offerIdFromUrl) {
        dispatch(replaceWithRegion(offerPageURL(offer)))
      } else if (urlLocation) {
        dispatch(replaceWithRegion('/trip-planner'))
      } else {
        dispatch(replaceWithRegion('/'))
      }
    }
  }, [dispatch, currentContact, orderId, cartId, offerIdFromUrl, urlLocation, offer])

  return {
    generateMultiDestinationTripMutation,
    handleGenerateTrip,
    handleTripSuccess,
  }
}
